/* eslint-disable max-lines */
import { useCallback, useContext } from "react";

// components
import { PaymentMethod, PaymentMethodMeta } from "@swiggy-private/payment-ui";

// contexts
import {
    AnalyticsContext,
    AnalyticsContextValue,
    CheckoutActionTypes,
    CheckoutContext,
    CheckoutRoute,
    DineoutCheckoutRoute,
    getBridgeSdk,
    GlobalHeaderState,
    MROFoodCartActionPayload,
    navigateToCart,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    useNavigate,
    useQuickCheckoutNavHandlers,
    isQuickCheckoutFlow,
    closeWebview,
} from "@swiggy-private/checkout-core";
import {
    ACTION_TYPES,
    NavigateAction,
    OnPayExtraParams,
    OrderInterruptionAction,
    PayAction,
} from "@swiggy-private/payment-types";
import {
    NavigateToScreenRequestPayload,
    NavigationScreenLinkTypes,
    NavigationTypes,
} from "@swiggy-private/native-bridge";

// contexts
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

// types
import { OrderInterruptionAlertPayload } from "@payment-client/interfaces/Checkout";

// helpers
import {
    getOrderInterruptionNavigateScreenLabel,
    isDuplicateOrder,
} from "@payment-client/helpers/OrderInterruptionHelper";
import { format } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// analytics
import {
    AdditionalDuplicateOrderClickParams,
    cardRateLimitClickEvent,
    duplicationOrderDialogClickActionEvent,
    maxPaymentRetriesAutoPayClickEvent,
    maxPaymentRetriesAutoPaySoftFailureClickEvent,
    pastPaymentPendingAutoPayClickEvent,
    surgeChargeOrderDialogClickActionEvent,
} from "@payment-client/analytics/events/OrderInterruption";

// constants
import {
    BAD_DEBIT_CODE,
    CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE,
    ORDER_TERMINAL_STATUS_CODE,
    PAST_PAYMENT_PENDING_STATUS_CODE,
    PAYMENT_MAX_RETRIES_EXCEEDED_SOFT_FAILURE_STATUS_CODE,
    PAYMENT_MAX_RETRIES_EXCEEDED_STATUS_CODE,
} from "@payment-client/constants/StatusCodes";
import { CLICK_ACTIONS, LABELS } from "@payment-client/analytics/constants";
import { USER_CONSENT_KEY } from "@payment-client/constants/OrderInterruptionAlert";
import { X_WILL_BE_CHARGED } from "@payment-client/constants/TextDict";
import { terminalStateAlertCtaClickEvent } from "@payment-client/analytics/events/OrderTerminalState";

interface OrderInterruptionAlertActionProps {
    payAction: (paymentMethodData: PaymentMethod<PaymentMethodMeta>, _extraParams?: OnPayExtraParams) => void;
    payload: OrderInterruptionAlertPayload;
}

interface UseOrderInterruptionAlertActionReturn {
    handleActionClick(
        action: OrderInterruptionAction,
        additionalDuplicateOrderClickParams: AdditionalDuplicateOrderClickParams,
    ): void;
    closeHalfCard(): void;
    getActionSubtext(errorStatusCode: number, actionType: ACTION_TYPES, amountToPay: number): string | undefined;
}

export function useOrderInterruptionAlertAction(
    props: OrderInterruptionAlertActionProps,
): UseOrderInterruptionAlertActionReturn {
    const { payAction, payload } = props;

    const { placeOrderData, errorStatusCode } = payload;

    const { setCheckoutState } = useContext(CheckoutContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const { setWithCoupon, billTotal } = useContext(PaymentPayContext);
    const navigateTo = useNavigate();
    const { handlePaymentsToQuickCheckout } = useQuickCheckoutNavHandlers();

    const isDuplicateOrderStatus = isDuplicateOrder(errorStatusCode);
    const sendAnalyticsEvents = useCallback(
        (fn: Function, navigationActionData: NavigateAction["actionData"], actionName = "") => {
            const cartId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartId);

            switch (errorStatusCode) {
                case PAYMENT_MAX_RETRIES_EXCEEDED_SOFT_FAILURE_STATUS_CODE:
                    if (navigationActionData.screen === NavigationScreenLinkTypes.TRACK) {
                        maxPaymentRetriesAutoPaySoftFailureClickEvent(
                            clickEvent,
                            CLICK_ACTIONS.CLICK_GOTO_TRACK,
                            billTotal,
                        );
                    }
                    break;
                case PAYMENT_MAX_RETRIES_EXCEEDED_STATUS_CODE:
                    maxPaymentRetriesAutoPayClickEvent(clickEvent, billTotal);
                    break;
                case CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE:
                    cardRateLimitClickEvent(clickEvent, LABELS.REVIEW_CART);
                    break;
                case PAST_PAYMENT_PENDING_STATUS_CODE:
                    pastPaymentPendingAutoPayClickEvent(clickEvent, billTotal);
                    break;
                case ORDER_TERMINAL_STATUS_CODE:
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.PAYMENT_WEB_ORDER_TERMINAL_ALERT_NAVIGATIONS,
                        type: actionName,
                    });
                    terminalStateAlertCtaClickEvent(clickEvent, actionName);
                    break;
                default:
                    cartId &&
                        fn(getOrderInterruptionNavigateScreenLabel(navigationActionData.screen), cartId, clickEvent);
                    break;
            }
        },
        [billTotal, clickEvent, errorStatusCode],
    );

    const sendPayAnalyticsEvents = useCallback(
        (fn: Function) => {
            const cartId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartId);

            if (errorStatusCode === CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE) {
                cardRateLimitClickEvent(clickEvent, LABELS.PROCEED);
            } else {
                cartId && fn(LABELS.PROCEED_AND_PAY, cartId, clickEvent);
            }
        },
        [clickEvent, errorStatusCode],
    );

    const getNavigationData = useCallback(
        (actionData: NavigateAction["actionData"], errorStatusCode: number): NavigateToScreenRequestPayload => {
            if (actionData.type === NavigationTypes.DEEPLINK) {
                return {
                    type: NavigationTypes.DEEPLINK,
                    link: actionData.link,
                    params: {
                        shouldCloseWebview: actionData.shouldCloseWebview,
                    },
                };
            }

            const data: NavigateToScreenRequestPayload = {
                type: NavigationTypes.SCREEN,
                link: actionData.screen,
            };

            switch (actionData.screen) {
                case NavigationScreenLinkTypes.ORDER_DETAIL:
                case NavigationScreenLinkTypes.IM_TRACK_SCREEN:
                    if (actionData.orderId) {
                        data.params = {
                            id: actionData.orderId,
                        };
                    }
                    break;
                case NavigationScreenLinkTypes.IM_CART:
                    data.params = {
                        isIMReviewCart: true,
                    };
                    break;
                case NavigationScreenLinkTypes.CART:
                    if (actionData.refresh) {
                        data.params = {
                            refresh: true,
                        };
                    }
                    break;
                case NavigationScreenLinkTypes.TRACK:
                    if (actionData.orderId) {
                        data.params = {
                            orderId: actionData.orderId,
                            shouldCloseWebview: actionData.shouldCloseWebview ?? true,
                            isIMOrder: actionData.isIMOrder ?? false,
                        };

                        if ([PAYMENT_MAX_RETRIES_EXCEEDED_STATUS_CODE].includes(errorStatusCode)) {
                            data.params.hideMakePaymentButton = true; // tell native to hide the make payment button for autopay
                        }
                    }
                    break;
            }

            return data;
        },
        [],
    );

    const closeHalfCard = useCallback(() => {
        setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
    }, [setCheckoutState]);

    const getActionSubtext = useCallback((errorStatusCode: number, actionType: ACTION_TYPES, amountToPay: number) => {
        if (errorStatusCode === CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE && actionType === ACTION_TYPES.PAY) {
            return format(X_WILL_BE_CHARGED, String(amountToPay));
        }
    }, []);

    const handleActionClick = useCallback(
        (
            action: OrderInterruptionAction,
            additionalDuplicateOrderClickParams: AdditionalDuplicateOrderClickParams,
        ): void => {
            const { actionType, actionData } = action;
            const { actionName, businessLine } = additionalDuplicateOrderClickParams;

            // send action event
            const fn = isDuplicateOrderStatus
                ? (actionName: string, cartId: string, clickEvent: AnalyticsContextValue["clickEvent"]): void =>
                      duplicationOrderDialogClickActionEvent(
                          actionName,
                          cartId,
                          clickEvent,
                          additionalDuplicateOrderClickParams,
                      )
                : (actionName: string, cartId: string, clickEvent: AnalyticsContextValue["clickEvent"]): void => {
                      const params = {
                          cartId,
                          actionName,
                          businessLine: businessLine || "",
                          errorStatusCode,
                      };
                      surgeChargeOrderDialogClickActionEvent(params, clickEvent);
                  };

            switch (actionType) {
                case ACTION_TYPES.PAY: {
                    const payActionData = actionData as PayAction["actionData"];
                    const requestParam = payActionData.userConsent;
                    if (!placeOrderData.extraParams) {
                        placeOrderData.extraParams = {};
                    }
                    if (errorStatusCode === CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE) {
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        placeOrderData.extraParams.force_validate_coupon = false;
                    }

                    // handling bad debit cases by passing updated amount in place order
                    if (errorStatusCode === BAD_DEBIT_CODE && !isEmpty(payActionData?.updatedAmount)) {
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        placeOrderData.extraParams.transaction_amount = payActionData?.updatedAmount;
                        // added new key to handle paymentAmount in place order api call
                        placeOrderData.extraParams.updatedPaymentAmount = String(payActionData?.updatedAmount);
                    }
                    placeOrderData.extraParams[requestParam] = true;
                    placeOrderData.extraParams[USER_CONSENT_KEY] = requestParam;
                    sendPayAnalyticsEvents(fn);
                    payAction(placeOrderData.paymentMethodData, placeOrderData.extraParams);
                    setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
                    break;
                }
                case ACTION_TYPES.NAVIGATE: {
                    const navigationActionData = actionData as NavigateAction["actionData"];

                    sendAnalyticsEvents(fn, navigationActionData, actionName);

                    const isSessionStorageAvailable =
                        typeof window !== "undefined" && typeof sessionStorage !== "undefined";

                    const checkIsFromCart = (cartType: string) =>
                        isSessionStorageAvailable &&
                        sessionStorage.getItem(cartType) === "true" &&
                        navigationActionData.screen === NavigationScreenLinkTypes.CART;

                    if (checkIsFromCart("isFromWebCart")) {
                        setWithCoupon(true);
                        setCheckoutState({
                            type: CheckoutActionTypes.SHOW_CART,
                            payload: null,
                        });
                        navigateToCart(navigateTo, {}, true);
                    } else if (checkIsFromCart("isFromGiftingCart")) {
                        setWithCoupon(true);
                        setCheckoutState({ type: CheckoutActionTypes.SHOW_GIFTING_CART, payload: null });
                        navigateTo(CheckoutRoute.GIFTING_CART, {}, true);
                    } else if (checkIsFromCart("isFromMROFoodCart") && isQuickCheckoutFlow()) {
                        setWithCoupon(true);
                        setCheckoutState({
                            type: CheckoutActionTypes.SHOW_MRO_FOOD_CART,
                            payload: { refreshCartDataForQuickCheckout: true },
                        });
                        navigateTo(CheckoutRoute.MRO_FOOD_CART, {}, true);
                    } else if (checkIsFromCart("isFromMROFoodCart")) {
                        setWithCoupon(true);
                        setCheckoutState({
                            type: CheckoutActionTypes.SHOW_MRO_FOOD_CART,
                            // skip tabs refetch to avoid the full bridge fetch
                            // helps reduce latency of the cart view when moving from payments to mro food cart
                            payload: { skipTabsRefetch: true } as MROFoodCartActionPayload,
                        });
                        navigateTo(CheckoutRoute.MRO_FOOD_CART, {}, true);
                    } else if (checkIsFromCart("isFromQuickCheckout")) {
                        setWithCoupon(true);
                        handlePaymentsToQuickCheckout();
                    } else if (checkIsFromCart("isFromDineoutCart")) {
                        setWithCoupon(true);
                        setCheckoutState({
                            type: CheckoutActionTypes.DINEOUT_TABLE_RESERVATION_CART,
                        });
                        navigateTo(DineoutCheckoutRoute.TABLE_RESERVATION_CART, {}, true);
                    } else if (navigationActionData.screen === NavigationScreenLinkTypes.IM_CART) {
                        const navigationData = getNavigationData(navigationActionData, errorStatusCode);
                        const { params } = navigationData as { params: { [key: string]: string } };
                        closeWebview({ params });
                    } else {
                        getBridgeSdk()
                            .navigateToScreen(getNavigationData(navigationActionData, errorStatusCode))
                            .catch(() => void 0);
                        setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
                    }
                    break;
                }
                case ACTION_TYPES.DISMISS:
                    if (errorStatusCode === PAYMENT_MAX_RETRIES_EXCEEDED_SOFT_FAILURE_STATUS_CODE) {
                        maxPaymentRetriesAutoPaySoftFailureClickEvent(
                            clickEvent,
                            CLICK_ACTIONS.CLICK_CONTINUE_PAYING,
                            billTotal,
                        );
                    }
                    closeHalfCard();
                    break;
            }
        },
        [
            isDuplicateOrderStatus,
            errorStatusCode,
            closeHalfCard,
            placeOrderData,
            sendPayAnalyticsEvents,
            payAction,
            setCheckoutState,
            sendAnalyticsEvents,
            setWithCoupon,
            navigateTo,
            handlePaymentsToQuickCheckout,
            getNavigationData,
            clickEvent,
            billTotal,
        ],
    );

    return { handleActionClick, closeHalfCard, getActionSubtext };
}
